import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IntegrationVendor } from '../../models/integration/integration-vendor.enum';
import { VENDOR_DEFINITIONS } from '../../models/integration/vendor-definitions.constant';
import { IntegrationProvider } from '../../provider/integration.provider';
import { SetupStep } from '../../reducer/integration.reducer';

/**
 * Modal step shown to the user. Local view state derived from `pendingSetup.step`.
 *
 * Same three-step shape as the Topal/PROFFIX connect modals: welcome (form) → testing
 * (spinner while the BE reads the tenant from the key and checks it against Accounto)
 * → error. No `created` step: on success the manage modal takes over and this modal
 * closes.
 *
 * The reducer uses `awaiting_consent` for the "BE is testing the credentials" phase
 * (a shared string across vendors); we map it to the local `'testing'` step here.
 */
export type AccountoModalViewStep = 'welcome' | 'testing' | 'error';

/**
 * Shape of an Accounto API key: a JWT, three base64url parts (REV-21119 D1). The BE decides whether it
 * carries a tenant and is still valid; the form only catches a pasted text that cannot be a key at all
 * (a tenant id, a URL, a key cut short by the copy), so the disabled Connect can say why.
 */
export const ACCOUNTO_API_KEY_PATTERN = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

@Component({
    selector: 'aumico-accounto-connect-modal',
    templateUrl: './accounto-connect-modal.component.html',
    styleUrls: ['./accounto-connect-modal.component.scss'],
})
export class AccountoConnectModalComponent implements OnInit, OnDestroy {
    @Output() public closed = new EventEmitter<void>();

    public step: AccountoModalViewStep = 'welcome';
    public errorMessage: string | null = null;

    /** Form values: local to the modal until submitted. NOT in NgRx until dispatch. */
    public displayName = '';
    public apiKey = '';
    /** Toggle the masking of the key (`type=password` ↔ `type=text`). */
    public showApiKey = false;

    public readonly vendorDefinition = VENDOR_DEFINITIONS.find(
        (v) => v.vendor === IntegrationVendor.ACCOUNTO,
    );

    private readonly destroy$ = new Subject<void>();

    public constructor(private readonly integrationProvider: IntegrationProvider) {}

    public ngOnInit(): void {
        this.integrationProvider
            .getPendingSetup$()
            .pipe(takeUntil(this.destroy$))
            .subscribe((setup) => {
                this.errorMessage = setup.error;

                if (setup.vendor !== IntegrationVendor.ACCOUNTO) return;

                // On success: the manage modal takes over. Close this immediately.
                if (setup.step === 'created') {
                    this.closed.emit();
                    return;
                }

                this.step = this.viewStepFor(setup.step);
            });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /** Both fields required; the key must look like a JWT (see `ACCOUNTO_API_KEY_PATTERN`). */
    public get isFormValid(): boolean {
        return this.missingFieldLabelKeys.length === 0 && !this.apiKeyInvalid;
    }

    /**
     * A pasted key that cannot be an Accounto key. Shown inline under the field so the disabled Connect
     * says why (guidelines §2.1). An empty field is not invalid but missing — that is `missingFieldLabelKeys`.
     * Surrounding whitespace from the copy is ignored (it is trimmed on submit).
     */
    public get apiKeyInvalid(): boolean {
        const key = this.apiKey.trim();
        return key.length > 0 && !ACCOUNTO_API_KEY_PATTERN.test(key);
    }

    /** Label keys of the required fields still empty, in form order — named next to the Connect button. */
    public get missingFieldLabelKeys(): string[] {
        const fields: [boolean, string][] = [
            [this.displayName.trim().length > 0, 'aumico_integration_setup_displayname_label'],
            [this.apiKey.trim().length > 0, 'aumico_accounto_step_config_apikey_label'],
        ];
        return fields.filter(([filled]) => !filled).map(([, labelKey]) => labelKey);
    }

    public onSubmit(): void {
        if (!this.isFormValid) return;
        this.integrationProvider.startAccountoConnect({
            displayName: this.displayName.trim(),
            // A JWT has no whitespace: a trailing newline from the copy would only make the BE reject it.
            apiKey: this.apiKey.trim(),
        });
    }

    public onToggleApiKey(): void {
        this.showApiKey = !this.showApiKey;
    }

    public onRetryFromError(): void {
        // Clear the reducer's pendingSetup slice FIRST: otherwise the next
        // `getPendingSetup$()` emission re-maps `step: 'error'` and yanks us straight
        // back to the error view. cancelSetup wipes the slice without closing the modal.
        this.integrationProvider.cancelSetup();
        this.errorMessage = null;
        this.step = 'welcome';
    }

    /**
     * Not while the key is tested: the POST would finish anyway and the BE would store the connection,
     * but with `pendingSetup` reset nothing picks it up, so auto-match never runs. Aborting the request
     * does not help (the BE may already have stored it). The modal waits for the answer; the template
     * offers no Cancel, close icon or Escape in that step, and this guard covers any other path.
     */
    public onCancel(): void {
        if (this.step === 'testing') return;
        this.integrationProvider.cancelSetup();
        this.closed.emit();
    }

    private viewStepFor(reducerStep: SetupStep): AccountoModalViewStep {
        switch (reducerStep) {
            case 'awaiting_consent':
                return 'testing';
            case 'error':
                return 'error';
            case 'created':
                // Handled separately by closing the modal: should never render.
                return 'welcome';
            case 'idle':
            default:
                return 'welcome';
        }
    }
}
